import React, { useEffect, useRef } from 'react';
import {
  MapPin,
  Navigation,
  ExternalLink,
  Play,
  Check,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/routes/paths';
import type { StopItem } from './types';

interface FieldActivityMapProps {
  stops: StopItem[];
}

export const FieldActivityMap: React.FC<FieldActivityMapProps> = ({ stops }) => {
  const navigate = useNavigate();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const currentStop = stops.find((s) => s.status === 'in_progress') || null;

  // Dynamic Leaflet instantiation
  useEffect(() => {
    let map: any = null;
    let isMounted = true;

    const initMap = async () => {

      if (!isMounted || !mapContainerRef.current) return;

      const L = (window as any).L;
      if (!L) return;

      // Pune center
      map = L.map(mapContainerRef.current, {
        zoomControl: false,
        attributionControl: false,
      }).setView([18.5204, 73.8567], 12);

      // High-resolution ESRI World Street Map (100% free of cost, zero API key, rock-solid CDN, zero 403)
      L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}', {
        maxZoom: 19,
        attribution: 'Tiles &copy; Esri &mdash; Street Basemap',
      }).addTo(map);

      // Add Zoom control top-right
      L.control.zoom({ position: 'topright' }).addTo(map);

      // Route line coordinates
      const latlngs: [number, number][] = stops.map((s) => [s.lat, s.lng]);

      // Draw polyline
      L.polyline(latlngs, {
        color: '#5b45d6',
        weight: 3.5,
        opacity: 0.8,
        dashArray: '6, 6',
      }).addTo(map);

      // Markers
      stops.forEach((stop, idx) => {
        const isCurrent = stop.status === 'in_progress';
        const isDone = stop.status === 'completed';

        const markerHtml = `
          <div style="
            background: ${isDone ? '#12b76a' : isCurrent ? '#5b45d6' : '#475467'};
            color: white;
            padding: 4px 8px;
            border-radius: 9999px;
            font-size: 10px;
            font-weight: bold;
            box-shadow: 0 4px 6px -1px rgba(0,0,0,0.15);
            display: flex;
            align-items: center;
            gap: 4px;
            white-space: nowrap;
            border: 2px solid white;
          ">
            <span>Stop ${idx + 1}</span>
            <span style="opacity: 0.85">· ${stop.time}</span>
          </div>
        `;

        const icon = L.divIcon({
          html: markerHtml,
          className: 'custom-map-pill',
          iconSize: [100, 24],
          iconAnchor: [50, 12],
        });

        const m = L.marker([stop.lat, stop.lng], { icon }).addTo(map);
        m.bindPopup(`<b>${stop.client}</b><br/>${stop.location}<br/><i>${stop.purpose}</i>`);
      });
    };

    initMap().catch(console.error);

    return () => {
      isMounted = false;
      if (map) map.remove();
    };
  }, [stops]);

  const googleMapsUrl = stops.length > 0
    ? `https://www.google.com/maps/dir/${stops.map((s) => `${s.lat},${s.lng}`).join('/')}`
    : 'https://maps.google.com/?q=18.5204,73.8567';

  return (
    <div className="flex flex-col justify-between rounded-card border border-border bg-card p-5 shadow-xs">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-outline/80 pb-3">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-container text-primary">
            <MapPin className="h-4 w-4" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-body-sm font-semibold uppercase tracking-wider text-on-surface">
                Field Activity & GPS (Today)
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-data-xs font-semibold text-success-text border border-success/30">
                <span className="h-1.5 w-1.5 rounded-full bg-success motion-reduce:animate-none" />
                Live GPS
              </span>
            </div>
            <div className="text-data-xs text-on-surface-subtle">{stops.length} stop{stops.length === 1 ? '' : 's'} plotted today</div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate(ROUTES.liveTracking)}
            className="inline-flex items-center gap-1 text-data-xs font-semibold text-primary hover:underline"
          >
            <span>Radar</span>
            <ExternalLink className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Map Container */}
      {/* isolate: the map's own layers (z-index 400+) stay inside this box
          instead of drawing over dialogs opened on the dashboard. */}
      <div className="relative isolate z-0 my-3 h-52 w-full overflow-hidden rounded-lg border border-border">
        <div ref={mapContainerRef} className="h-full w-full" />

        {stops.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-card/70 text-body-sm font-medium text-on-surface-variant">
            No geocoded visits planned for today.
          </div>
        )}

        {/* Floating Quick Action Overlay */}
        {stops.length > 0 && (
          <div className="absolute bottom-3 left-3 z-[400] flex items-center gap-2 rounded-lg bg-card/95 p-1.5 shadow-surface-1 backdrop-blur-xs border border-border">
            <div
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-body-sm font-semibold ${
                currentStop ? 'bg-emerald-600 text-on-primary' : 'bg-surface-container text-on-surface-variant'
              }`}
            >
              {currentStop ? <Check className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
              <span>{currentStop ? `At ${currentStop.client}` : 'No active check-in'}</span>
            </div>

            <a
              href={googleMapsUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface-variant px-2.5 py-1.5 text-body-sm font-semibold text-on-surface-variant hover:bg-surface-container transition"
            >
              <Navigation className="h-3.5 w-3.5 text-info-text" />
              <span>Turn-by-Turn GPS</span>
            </a>
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div className="rounded-lg border border-border bg-surface-variant/70 p-3 flex items-center justify-between text-body-sm">
        <span className="text-on-surface-subtle">Current Stop:</span>
        <span className="font-semibold text-on-surface">
          {currentStop ? `${currentStop.client} · ${currentStop.location}` : 'None in progress'}
        </span>
      </div>
    </div>
  );
};

